import { PHONE_INPUT_MASK } from 'constants/utils';

import { Grid } from '@mui/material';
import { ErrorContainer, InputField, LabelComponent, Stack } from 'components';
import AccordionComponent from 'components/accordion/accordion';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import { useFormikContext } from 'formik';
import MaskedInput from 'react-text-mask';
import { displayErrorStyling } from 'utils/errorMessages';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FormValues as FormValuesWithPcpPcc } from '../../clientFormWithPccPcp';
import { FormValues as FormValuesWithBilling } from '../../newClientForm';
import { isSectionInvalid } from '../personalDetails/personalDetails';
import SectionTitle from '../sectionTitle/sectionTitle';

const fieldNames = ['name', 'organization', 'phoneNumber', 'email'];

const SupportSystem = () => {
  const { enableBillingUpdates } = useFlags();
  type FormValues = typeof enableBillingUpdates extends true
    ? FormValuesWithBilling
    : FormValuesWithPcpPcc;
  const formik = useFormikContext<FormValues>();
  const { isTablet } = useResponsive();

  return (
    <AccordionComponent
      accordionTabId="third"
      title="Support System"
      showWarning={
        isSectionInvalid(formik?.errors, ['supportPeople', 'careTeam']) ||
        isSectionInvalid(formik?.errors?.primaryCareProvider, fieldNames) ||
        isSectionInvalid(formik?.errors?.primaryCareCoordinator, fieldNames)
      }
    >
      <Stack direction="column" gap="24px" pt="16px">
        <Stack gap="24px" mt="24px" mb="12px" data-test-id="primary-care-provider">
          <SectionTitle>Primary Care Provider</SectionTitle>
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            mt="24px"
            ml="-16px"
            mb="-16px"
          >
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Name" />
                <InputField
                  value={formik.values.primaryCareProvider?.name ?? ''}
                  name="primaryCareProvider.name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareProvider?.name &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareProvider?.name
                  }
                  errors={formik.errors.primaryCareProvider}
                  name="name"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Organization" />
                <InputField
                  value={formik.values.primaryCareProvider?.organization ?? ''}
                  name="primaryCareProvider.organization"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareProvider?.organization &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareProvider?.organization
                  }
                  errors={formik.errors.primaryCareProvider}
                  name="organization"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Phone" />
                <MaskedInput
                  guide={false}
                  mask={PHONE_INPUT_MASK}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.primaryCareProvider?.phoneNumber ?? ''}
                  render={(ref, props) => (
                    <InputField
                      classnamesProps={displayErrorStyling(
                        // @ts-expect-error incorrect formik type because of possible undefined
                        formik.touched.primaryCareProvider?.phoneNumber,
                        // @ts-expect-error incorrect formik type because of possible undefined
                        formik.errors.primaryCareProvider?.phoneNumber
                      )}
                      placeholder="(000) 000-0000"
                      name="primaryCareProvider.phoneNumber"
                      onChange={props.onChange}
                      onBlur={props.onBlur}
                      value={formik.values.primaryCareProvider?.phoneNumber ?? ''}
                      ref={(el) => {
                        if (el) {
                          ref(el);
                        }
                      }}
                    />
                  )}
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareProvider?.phoneNumber &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareProvider?.phoneNumber
                  }
                  errors={formik.errors.primaryCareProvider}
                  name="phoneNumber"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Email" />
                <InputField
                  value={formik.values.primaryCareProvider?.email ?? ''}
                  name="primaryCareProvider.email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="email@email.com"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareProvider?.email &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareProvider?.email
                  }
                  errors={formik.errors.primaryCareProvider}
                  name="email"
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
        <Stack gap="24px" mt="24px" mb="12px" data-test-id="care-coordinator">
          <SectionTitle>Care Coordinator</SectionTitle>
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            mt="24px"
            ml="-16px"
            mb="-8px"
          >
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Name" />
                <InputField
                  value={formik.values.primaryCareCoordinator?.name ?? ''}
                  name="primaryCareCoordinator.name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareCoordinator?.name &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareCoordinator?.name
                  }
                  errors={formik.errors.primaryCareCoordinator}
                  name="name"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Organization" />
                <InputField
                  value={formik.values.primaryCareCoordinator?.organization ?? ''}
                  name="primaryCareCoordinator.organization"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareCoordinator?.organization &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareCoordinator?.organization
                  }
                  errors={formik.errors.primaryCareCoordinator}
                  name="organization"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Phone" />
                <MaskedInput
                  guide={false}
                  mask={PHONE_INPUT_MASK}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.primaryCareCoordinator?.phoneNumber ?? ''}
                  render={(ref, props) => (
                    <InputField
                      classnamesProps={displayErrorStyling(
                        // @ts-expect-error incorrect formik type because of possible undefined
                        formik.touched.primaryCareCoordinator?.phoneNumber,
                        // @ts-expect-error incorrect formik type because of possible undefined
                        formik.errors.primaryCareCoordinator?.phoneNumber
                      )}
                      placeholder="(000) 000-0000"
                      name="primaryCareCoordinator.phoneNumber"
                      onChange={props.onChange}
                      onBlur={props.onBlur}
                      value={formik.values.primaryCareCoordinator?.phoneNumber ?? ''}
                      ref={(el) => {
                        if (el) {
                          ref(el);
                        }
                      }}
                    />
                  )}
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareCoordinator?.phoneNumber &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareCoordinator?.phoneNumber
                  }
                  errors={formik.errors.primaryCareCoordinator}
                  name="phoneNumber"
                />
              </Stack>
            </Grid>
            <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
              <Stack width="100%">
                <LabelComponent text="Email" />
                <InputField
                  value={formik.values.primaryCareCoordinator?.email ?? ''}
                  name="primaryCareCoordinator.email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="email@email.com"
                />
                <ErrorContainer
                  visible={
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.touched.primaryCareCoordinator?.email &&
                    // @ts-expect-error incorrect formik type because of possible undefined
                    formik.errors.primaryCareCoordinator?.email
                  }
                  errors={formik.errors.primaryCareCoordinator}
                  name="email"
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
        <Stack gap="24px" mt="16px" mb="16px" data-test-id="profile-referral">
          <SectionTitle>Other Care Team</SectionTitle>
          <Stack height="100px">
            <TextAreaInputField
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.careTeam ?? ''}
              placeholder="List any other members of the client’s health and social care teams"
              name="careTeam"
            />
            <ErrorContainer
              visible={formik.errors.careTeam && formik.touched.careTeam}
              name="careTeam"
              errors={formik.errors}
            />
          </Stack>
        </Stack>
        <Stack gap="24px" mt="24px" mb="16px" data-test-id="profile-support">
          <SectionTitle>Social Supports</SectionTitle>
          <Stack height="100px">
            <TextAreaInputField
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.supportPeople ?? ''}
              placeholder="List the people who provide social support to the client, e.g., a neighbor, family or friend"
              name="supportPeople"
            />
            <ErrorContainer
              visible={formik.errors.supportPeople && formik.touched.supportPeople}
              name="supportPeople"
              errors={formik.errors}
            />
          </Stack>
        </Stack>
      </Stack>
    </AccordionComponent>
  );
};

export default SupportSystem;
